import { useCallback, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

export interface PromptOptions {
  title: string;
  message?: string;
  label?: string;
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

export function PromptDialog({
  open,
  options,
  value,
  onChange,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  options: PromptOptions | null;
  value: string;
  onChange: (v: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!options) return null;
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={options.title}
      size="sm"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onCancel}>
            {options.cancelLabel ?? 'Cancel'}
          </Button>
          <Button type="button" onClick={onConfirm}>
            {options.confirmLabel ?? 'OK'}
          </Button>
        </>
      }
    >
      {options.message && <p className="mb-3 text-sm text-slate-600 dark:text-slate-300">{options.message}</p>}
      <Input label={options.label ?? 'Value'} value={value} onChange={(e) => onChange(e.target.value)} />
    </Modal>
  );
}

export function usePrompt() {
  const [state, setState] = useState<{
    open: boolean;
    options: PromptOptions;
    value: string;
    resolve: (value: string | null) => void;
  } | null>(null);

  const prompt = useCallback((options: PromptOptions) => {
    return new Promise<string | null>((resolve) => {
      setState({
        open: true,
        options,
        value: options.defaultValue ?? '',
        resolve,
      });
    });
  }, []);

  const handleConfirm = () => {
    state?.resolve(state.value);
    setState(null);
  };

  const handleCancel = () => {
    state?.resolve(null);
    setState(null);
  };

  const dialog = (
    <PromptDialog
      open={Boolean(state?.open)}
      options={state?.options ?? null}
      value={state?.value ?? ''}
      onChange={(v) => setState((s) => (s ? { ...s, value: v } : s))}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );

  return { prompt, dialog };
}
